import type { DriveFolderType, SerializedMeterModel } from '@enmon/commons/lib/tools';
import { useServices } from 'containers/services';
import { useAppNotifications } from 'hooks';
import { useMutation, type UseMutationResult, useQueryClient } from 'react-query';
import { getErrorMessage } from 'utils/getErrorMessage';
import { MUTATION_KEYS, QUERY_KEYS } from 'utils/queryKeys';

export interface UploadMeterModelFilesArg {
  modelId: SerializedMeterModel['id'];
  folderType: DriveFolderType;
  files: File[];
}

export interface UploadMeterModelFilesResult {
  success: boolean;
  message: string;
}

export const useUploadMeterModelFiles = (): UseMutationResult<
  UploadMeterModelFilesResult,
  unknown,
  UploadMeterModelFilesArg
> => {
  const queryClient = useQueryClient();
  const { meterModelsApiClient } = useServices();
  const notify = useAppNotifications();

  return useMutation({
    mutationKey: MUTATION_KEYS.meterModels.uploadFiles(),
    mutationFn: async (arg: UploadMeterModelFilesArg) => await meterModelsApiClient.uploadFiles(arg),
    onError: error => {
      notify.error({
        message: 'There was an error while uploading files',
        description: error instanceof Error ? getErrorMessage(error) : undefined,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(
        QUERY_KEYS.meterModels.files(variables.modelId, { folderType: variables.folderType }),
      );
      notify.success({
        message: data.message ?? 'Files uploaded successfully',
      });
    },
  });
};
